import {RestDataLoader} from "../../../dashboard/widgets/unilever/RestDataLoader";
import {LocationHaccpResponse} from "./HaccpController";

const DEFAULT_ERROR_MESSAGE = "Error in requesting data";

class Service {
    public getEquipmentInfo(ID: string): Promise<any> {
        return new Promise<string>((resolve, reject) => {
            Ajax.ajaxRequestForJson(
                {
                    url: "equipment/" + ID + "/info",
                    type: "GET",
                    success: (response) => {
                        resolve(response);
                    },
                    error : reject,
                },
            );
        });
    }

    public getLocationHaccp(guid: string,
                            startDate?: string,
                            endDate?: string,
                            unit?: string,
                            ): Promise<LocationHaccpResponse[]> {
        const availableOnCloud = true;
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.loadData(
                "locations/" + guid + "/haccp",
                {startDate, endDate, unit, availableOnCloud},
                (result) => resolve(result.list),
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        });
    }

    public getLocationEquipments(guid: string): Promise<any> {
        return new Promise<string>((resolve, reject) => {
            RestDataLoader.loadData("locations/" + guid + "/equipment", {}, (data) => {resolve(data); }, () => {
                reject("Error on getLocationEquipment");
            });
        });
    }

    public getEquipmentsModels(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.loadData(
                "equipment/models",
                {},
                resolve,
                reject,
            );
        });
    }

    public getEquipmentData(GUID: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.loadData(
                "equipment/" + GUID,
                {},
                resolve,
                reject,
            );
        });
    }

    public getEquipmentModel(GUID: string): Promise<any> {
        return this.getEquipmentData(GUID).then((result) => result.core.model);
    }

    public getEquipmentModelContentType(GUID: string, unit?: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.loadData(
                "equipment/models/" + GUID + "/contentTypes",
                {unit},
                resolve,
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        });
    }

    public getEquipmentDiagnosticsTypes(guid: string, availableOnCloud?: boolean): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.loadData(
                "equipment/" + guid + "/diagnostics/types",
                {availableOnCloud},
                resolve,
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        });
    }

    public getEquipmentHaccp(guid: string, startDate?: string, endDate?: string, unit?: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.loadData(
                "equipment/" + guid + "/haccp",
                {startDate, endDate, unit},
                resolve,
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        });
    }

    public exportAllReports(
        guid: string,
        granularity: string,
        unit: string,
        startDate?: string,
        endDate?: string,
        filename?: string,
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.downloadSpreadsheetKeepingFileName(
                "haccp/report",
                {
                    guid,
                    unit,
                    granularity,
                    startDate,
                    endDate,
                },
                resolve,
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        });
    }

    public exportLocationReport(
        guid: string,
        granularity: string,
        unit: string,
        startDate?: string,
        endDate?: string,
        filename?: string,
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.downloadSpreadsheetKeepingFileName(
                "haccp/location/" + guid + "/report",
                {
                    granularity,
                    unit,
                    startDate,
                    endDate,
                },
                resolve,
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        });
    }

    public getHaccpConfigReport(
        guid: string,
        diagnosticType: string,
        granularity: string,
        unit?: string,
        startDate?: string,
        endDate?: string,
    ): Promise<any> {
        return new Promise<any>(((resolve, reject) => {
            RestDataLoader.loadData(
                "equipment/" + guid + "/haccp/" + diagnosticType + "/report",
                {granularity, startDate, endDate, unit},
                resolve,
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        }));
    }

    public exportHaccpConfigReport(
        guid: string,
        diagnosticType: string,
        granularity: string,
        unit: string,
        startDate?: string,
        endDate?: string,
        filename?: string,
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.downloadSpreadsheetKeepingFileName(
                "equipment/" + guid + "/haccp/" + diagnosticType + "/report",
                {
                    granularity,
                    unit,
                    startDate,
                    endDate,
                },
                resolve,
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        });
    }

    public createNewHaccpConfiguration(
        equipmentGuid: string,
        diagnosticGuid: string,
        minTemperature: number,
        maxTemperature: number,
        productCategoryGuid: string,
        unit: string,
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.postJSON(
                "equipment/" + equipmentGuid + "/haccp?unit=" + unit,
                {
                    diagnosticGuid,
                    maxTemperature,
                    minTemperature,
                    productCategoryGuid,
                },
                resolve,
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        });
    }

    public editHaccpConfiguration(
        equipmentGuid: string,
        diagnosticGuid: string,
        minTemperature: number,
        maxTemperature: number,
        productCategoryGuid: string,
        unit: string,
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.putJSON(
                "equipment/" + equipmentGuid + "/haccp/" + diagnosticGuid + "?unit=" + unit,
                {
                    maxTemperature,
                    minTemperature,
                    productCategoryGuid,
                },
                resolve,
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        });
    }

    public deleteHaccpConfiguration(
        equipmentGuid: string,
        diagnosticGuid: string,
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            RestDataLoader.deleteJSON(
                "equipment/" + equipmentGuid + "/haccp/" + diagnosticGuid,
                {},
                resolve,
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        });
    }

    public getLocation(
        locationGuid: string,
    ) {
        return new Promise<any>(((resolve, reject) => {
            RestDataLoader.loadData(
                "locations/" + locationGuid,
                {},
                resolve,
                () => {
                    reject(DEFAULT_ERROR_MESSAGE);
                },
            );
        }));
    }
}

export const HaccpService = new Service();
